import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { User } from "../../models/User";
import { AuthService } from "../../services/authService";
import { UserService } from "../../services/userService";
import { UpdateValues } from "../../validation/RegisterValidation";
import { customerService } from "../../services/customerService";
import { monetaryZonesServices } from "../../services/monetaryZonesServices";
import { CreateCountryDto } from "../../models/MonetaryZones";

export interface AuthState {
  currentUser: User | null;
  errors: string | null;
  loading: boolean; // Loading state added
  otpVerified: boolean;
  customerMode: string;
  countries: CreateCountryDto[];
}

const initialState: AuthState = {
  currentUser: null,
  errors: null,
  loading: false,
  otpVerified: false,
  customerMode: 'SANDBOX',
  countries:[],
};

export const switchCustomerMode = createAsyncThunk<string, void>(
  "customer/mode",
  async (_, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState() as { auth: AuthState };
      const response = await customerService.switchCustomerMode();

      if (response.status === 2000) {
        return response.data.mode;
      } else {
        return rejectWithValue("Failed to switch mode");
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to switch mode");
    }
  }
);


export const logout = createAsyncThunk<void>(
  "auth/logout",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      await AuthService.logout(); // Assuming this sends the logout request
      dispatch(resetAuthState()); // Reset the auth state upon successful logout
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Logout failed");
    }
  }
);

export const sendOtp = createAsyncThunk<void, string>(
  "auth/sendOtp",
  async (email, { rejectWithValue }) => {
    try {
      await AuthService.sendOtp(email); // Adjust this method based on your service
    } catch (error) {
      return rejectWithValue("Failed to send OTP");
    }
  }
);

// Thunk to verify OTP
export const verifyOtp = createAsyncThunk<void, { email: string; otp: string }>(
  "auth/verifyOtp",
  async (data, { rejectWithValue }) => {
    try {
      await AuthService.verifyOtp(data); // Adjust this method based on your service
    } catch (error) {
      return rejectWithValue("Invalid OTP");
    }
  }
);

// Thunk to fetch the current user
export const getCurrentUser = createAsyncThunk<User>(
  "auth/current",
  async (_, { rejectWithValue }) => {
    try {
      const res = await AuthService.getCurrentUser();
      return res.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to get user"
      );
    }
  }
);

export const updateUserProfile = createAsyncThunk<User, Partial<UpdateValues>>(
  "auth/updateUserProfile",
  async (userData, { rejectWithValue, dispatch }) => {
    try {
      const updatedUser = await UserService.updateUser(userData);
      dispatch(getCurrentUser());
      return updatedUser;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update user"
      );
    }
  }
);


export const getAllCountry = createAsyncThunk<
  CreateCountryDto[]
>(
  "countries/all",
  async (_, thunkAPI) => {
    try {
      const res = await monetaryZonesServices.getAllCountry();
      return res.data;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error.message ||
        "Failed to fetch transactions";
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    resetAuthState: (state) => {
      // Helper reducer to reset the auth state
  
      state.currentUser = null;
      state.errors = null;
      state.loading = false;
      state.otpVerified = false;
      state.customerMode = 'SANDBOX'
    },
    setCustomerMode: (state, action: { payload: string }) => {
      state.customerMode = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(logout.pending, (state) => {
        state.loading = true;
        state.errors = null;
      })
      .addCase(logout.fulfilled, (state) => {
        state.loading = false;
        state.errors = null;
      })
      .addCase(logout.rejected, (state, action) => {
        state.loading = false;
        state.errors = action.payload as string;
      })
      .addCase(updateUserProfile.pending, (state) => {
        state.loading = true;
        state.errors = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.currentUser = { ...state.currentUser, ...action.payload };
        state.loading = false;
        state.errors = null;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.errors = action.payload as string;
      })
      .addCase(sendOtp.pending, (state) => {
        state.loading = true;
        state.errors = null;
      })
      .addCase(sendOtp.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(sendOtp.rejected, (state, action) => {
        state.loading = false;
        state.errors = action.error.message || "Failed to send OTP";
      })

      // Handle OTP verification
      .addCase(verifyOtp.pending, (state) => {
        state.loading = true;
        state.errors = null;
      })
      .addCase(verifyOtp.fulfilled, (state) => {
        state.loading = false;
        state.otpVerified = true; // OTP verified successfully
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.loading = false;
        state.errors = action.error.message || "Invalid OTP";
      })
      // Handle the pending state for getCurrentUser
      .addCase(getCurrentUser.pending, (state) => {
        state.loading = true; // Set loading to true when the request is pending
        state.errors = null;
      })
      // Handle the fulfilled state for getCurrentUser
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.currentUser = action.payload;
        if (action.payload.customer) {
          state.customerMode = action.payload.customer.mode;
        }
        state.loading = false; // Set loading to false when the request is fulfilled
        state.errors = null;
      })

         // Gestion du changement de mode
         .addCase(switchCustomerMode.pending, (state) => {
          state.loading = true;
          state.errors = null;
        })
        .addCase(switchCustomerMode.fulfilled, (state, action) => {
          state.customerMode = action.payload;
          // Mettre aussi à jour le mode dans currentUser si nécessaire
          if (state.currentUser?.customer) {
            state.currentUser.customer.mode = action.payload;
          }
          state.loading = false;
          state.errors = null;
        })
        .addCase(switchCustomerMode.rejected, (state, action) => {
          state.loading = false;
          state.errors = action.payload as string;
        })
        
      // Handle the rejected state for getCurrentUser
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.loading = false; // Set loading to false when the request is rejected
        state.errors = action.error.message || "Failed to fetch user";
      })
      .addCase(getAllCountry.pending, (state) => {
        state.loading = true;
        state.errors = null;
      })
      .addCase(getAllCountry.fulfilled, (state, action) => {
        state.countries = action.payload; // Update with fetched countries
        state.loading = false;
        state.errors = null;
      })
      .addCase(getAllCountry.rejected, (state, action) => {
        state.loading = false;
        state.errors = action.payload as string;
      });
  },
});

export const { resetAuthState } = authSlice.actions;

export default authSlice.reducer;
